import { API } from "@/services/api";
import { IAPIResponse } from "@/services/api.types";
import { settingsEndpoints } from "@/constant/endpoints";
import { buildQueryString } from "@/utils/common.utils";
import {
  ChangePasswordPayload,
  ChangePasswordResult,
  DeleteCheck,
  LoginHistoryParams,
  LoginHistoryResponse,
  MyProfile,
  MyProfilePayload,
  PhoneLoginTogglePayload,
  PhoneVerifyConfirmPayload,
  PhoneVerifyStartPayload,
  PhoneVerifyStartResult,
  OrgProfile,
  OrgProfilePayload,
  StatusDefault,
  StatusSnippet,
  StatusSnippetDeleted,
  StatusSnippetPatch,
  StatusSnippetPayload,
  StatusTemplatesPayload,
} from "@/types/settings.types";

export const getMyProfileService = async (): Promise<MyProfile> => {
  const response = await API.Get<MyProfile>(settingsEndpoints.myProfile);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const updateMyProfileService = async (
  payload: MyProfilePayload
): Promise<IAPIResponse<MyProfile>> =>
  API.Patch<MyProfilePayload, MyProfile>(settingsEndpoints.myProfile, payload);

export const startPhoneVerificationService = async (
  payload: PhoneVerifyStartPayload
): Promise<IAPIResponse<PhoneVerifyStartResult>> =>
  API.Post<PhoneVerifyStartPayload, PhoneVerifyStartResult>(
    settingsEndpoints.phoneVerifyStart, payload
  );

export const confirmPhoneVerificationService = async (
  payload: PhoneVerifyConfirmPayload
): Promise<IAPIResponse<MyProfile>> =>
  API.Post<PhoneVerifyConfirmPayload, MyProfile>(settingsEndpoints.phoneVerifyConfirm, payload);

export const setPhoneLoginService = async (
  payload: PhoneLoginTogglePayload
): Promise<IAPIResponse<MyProfile>> =>
  API.Post<PhoneLoginTogglePayload, MyProfile>(settingsEndpoints.phoneLogin, payload);

export const getOrgProfileService = async (orgId: string): Promise<OrgProfile> => {
  const response = await API.Get<OrgProfile>(settingsEndpoints.orgProfile(orgId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const updateOrgProfileService = async (
  orgId: string,
  payload: OrgProfilePayload
): Promise<IAPIResponse<OrgProfile>> =>
  API.Patch<OrgProfilePayload, OrgProfile>(settingsEndpoints.orgProfile(orgId), payload);

export const changePasswordService = async (
  payload: ChangePasswordPayload
): Promise<IAPIResponse<ChangePasswordResult>> =>
  API.Post<ChangePasswordPayload, ChangePasswordResult>(settingsEndpoints.changePassword, payload);

export const getDeleteCheckService = async (): Promise<DeleteCheck> => {
  const response = await API.Get<DeleteCheck>(settingsEndpoints.deleteCheck);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const deleteAccountService = async (confirm: string): Promise<IAPIResponse<{ ok: boolean }>> =>
  API.Delete<{ confirm: string }, { ok: boolean }>(settingsEndpoints.deleteAccount, { confirm });

export const getLoginHistoryService = async (
  params: LoginHistoryParams = {}
): Promise<LoginHistoryResponse> => {
  const response = await API.Get<LoginHistoryResponse>(
    `${settingsEndpoints.loginHistory}${buildQueryString(params)}`
  );
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const getStatusTemplatesService = async (
  orgId: string
): Promise<StatusTemplatesPayload> => {
  const response = await API.Get<StatusTemplatesPayload>(settingsEndpoints.statusTemplates(orgId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const setStatusDefaultService = async (
  orgId: string,
  slot: string,
  body: string
): Promise<IAPIResponse<StatusDefault>> =>
  API.Put<{ body: string }, StatusDefault>(
    settingsEndpoints.statusTemplateDefault(orgId, slot),
    { body }
  );

export const resetStatusDefaultService = async (
  orgId: string,
  slot: string
): Promise<IAPIResponse<StatusDefault>> =>
  API.Delete<undefined, StatusDefault>(settingsEndpoints.statusTemplateDefault(orgId, slot));

export const createStatusSnippetService = async (
  orgId: string,
  payload: StatusSnippetPayload
): Promise<IAPIResponse<StatusSnippet>> =>
  API.Post<StatusSnippetPayload, StatusSnippet>(settingsEndpoints.statusSnippets(orgId), payload);

export const updateStatusSnippetService = async (
  orgId: string,
  id: string,
  payload: StatusSnippetPatch
): Promise<IAPIResponse<StatusSnippet>> =>
  API.Patch<StatusSnippetPatch, StatusSnippet>(settingsEndpoints.statusSnippet(orgId, id), payload);

export const deleteStatusSnippetService = async (
  orgId: string,
  id: string
): Promise<IAPIResponse<StatusSnippetDeleted>> =>
  API.Delete<undefined, StatusSnippetDeleted>(settingsEndpoints.statusSnippet(orgId, id));
